import { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  useFetcher,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Print } from '@mui/icons-material';
import { Box, Button, Divider, Stack, styled } from '@mui/material';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { CancelOrderUploadDialog } from '@ldt/features/orders/components/cancel_order_upload_dialog';
import {
  ConfirmPathologyFaxDialog,
  PathologyFaxConfirmPayload,
} from '@ldt/features/orders/components/confirm_pathology_fax_dialog';
import {
  RejectReportDialog,
  RejectReportFormData,
} from '@ldt/features/orders/components/reject_report_dialog';
import {
  ShareTestReportDialog,
  ShareTestReportFormData,
} from '@ldt/features/orders/components/share_test_report_dialog';
import { LdtSlideImageSection } from '@ldt/features/orders/components/slide_image_section';
import { UploadFilesDialog } from '@ldt/features/orders/components/upload_files_dialog';
import {
  PathologyFaxDialogMode,
  shouldSkipPathologyFaxConfirmationDialog,
} from '@ldt/features/orders/utils/pathology_fax_action';
import type { LdtWorkflowDefinition } from '@ldt/workflows/types';
import { LdtOrderAction } from '@ldt/workflows/types';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Caution } from 'src/components/icons/caution';
import { Warning } from 'src/components/icons/warning';
import { SlideImageDownloadRow } from 'src/distribution_platforms/features/components/slide_image_download_row';
import {
  useWorkflow,
  useWorkflowType,
} from 'src/distribution_platforms/features/hooks/use_workflow';
import { findMostRecentSlideImage } from 'src/distribution_platforms/features/utils/slide_image_utils';
import { useUser } from 'src/hooks/use_auth';
import { LdtOrderDetailFieldsFragment } from 'src/types';
import { OrderAction, OrderActionResult } from 'src/types/loaders';
import { WorkflowType } from 'src/types/workflows';
import {
  notifyError,
  notifySuccess,
  isCustomerSuccessOrSalesUser,
} from 'src/utils';

export const IMAGE_QC_REJECT_DIALOG_TITLE = 'Confirm Image QC Reject';

export const IMAGE_QC_REJECT_REASON_INCORRECT_PATIENT =
  'The image is not for the correct patient.';

// Styled Components
const ActionsContainer = styled(Box)(({ theme }) => ({
  margin: `${theme.spacing(-1)} 0`,
}));

const ActionButton = styled(Button)({
  alignSelf: 'flex-start',
  textAlign: 'left',
});

const ActionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

const ActionItemContainer = styled(Box)({
  // Container for each action button
});

interface OrderActionsProps {
  order: LdtOrderDetailFieldsFragment;
  orderRepository: LdtOrderRepository;
}

/**
 * Component that renders order actions based on user role, order status, and workflow type.
 */
export const OrderActions: FC<OrderActionsProps> = ({
  order,
  orderRepository,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organization } = useParams();
  const userInfo = useUser();
  const workflow = useWorkflow() as LdtWorkflowDefinition;
  const workflowType = useWorkflowType();

  // One fetcher per action so we can track loading/state individually
  const fetcherDisplayRequisition = useFetcher();
  const fetcherCancel = useFetcher();
  const fetcherRejectDraftReport = useFetcher();
  const fetcherDisplayPatientReport = useFetcher();
  const fetcherDisplayDeIdentifiedReport = useFetcher();
  const fetcherShareTestReport = useFetcher();
  const fetcherRefaxReport = useFetcher();
  const fetcherFaxTestReport = useFetcher();
  const fetcherRevertToQcPending = useFetcher();
  const fetcherApproveQC = useFetcher();
  const fetcherImageQcApprove = useFetcher();
  const fetcherImageQcReject = useFetcher();
  const fetcherPathologyFax = useFetcher();

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelUploadDialogOpen, setCancelUploadDialogOpen] = useState(false);
  const [uploadFilesDialogOpen, setUploadFilesDialogOpen] = useState(false);
  const [pathologyFaxDialogMode, setPathologyFaxDialogMode] =
    useState<PathologyFaxDialogMode | null>(null);
  const [revertToQcPendingDialogOpen, setRevertToQcPendingDialogOpen] =
    useState(false);
  const [imageQcApproveDialogOpen, setImageQcApproveDialogOpen] =
    useState(false);
  const [imageQcRejectDialogOpen, setImageQcRejectDialogOpen] = useState(false);
  const [faxTestReportDialogOpen, setFaxTestReportDialogOpen] = useState(false);
  const [refaxReportDialogOpen, setRefaxReportDialogOpen] = useState(false);
  const [shareTestReportDialogOpen, setShareTestReportDialogOpen] =
    useState(false);
  const [rejectReportDialogOpen, setRejectReportDialogOpen] = useState(false);
  const lastProcessedDisplayRequisitionRef = useRef<unknown>(null);
  const lastProcessedCancelRef = useRef<unknown>(null);
  const lastProcessedRejectDraftReportRef = useRef<unknown>(null);
  const lastProcessedDisplayPatientReportRef = useRef<unknown>(null);
  const lastProcessedDisplayDeIdentifiedReportRef = useRef<unknown>(null);
  const lastProcessedShareTestReportRef = useRef<unknown>(null);
  const lastProcessedRefaxReportRef = useRef<unknown>(null);
  const lastProcessedFaxTestReportRef = useRef<unknown>(null);
  const lastProcessedRevertToQcPendingRef = useRef<unknown>(null);
  const lastProcessedApproveQCRef = useRef<unknown>(null);
  const lastProcessedImageQcApproveRef = useRef<unknown>(null);
  const lastProcessedImageQcRejectRef = useRef<unknown>(null);
  const lastProcessedPathologyFaxRef = useRef<unknown>(null);

  const isCSOrSalesUser = isCustomerSuccessOrSalesUser();

  const missingInformationMessages =
    workflow.getOrderMissingInfoMessages(order);

  const [hasValidationErrors, setHasValidationErrors] = useState<boolean>(true);

  useEffect(() => {
    const checkValidationErrors = async () => {
      const hasErrors = await workflow.checkOrderValidationErrors({
        order,
      });
      setHasValidationErrors(hasErrors);
    };

    checkValidationErrors();
  }, [order, isCSOrSalesUser, workflow]);

  const availableActions = workflow.getAvailableActions({
    order,
    missingInformationMessages,
    hasValidationErrors,
    userInfoId: userInfo.id,
  });

  // Handler for DISPLAY REQUISITION action
  const handleDisplayRequisition = useCallback(() => {
    const formData = new FormData();
    formData.append('action', OrderAction.DISPLAY_REQUISITION);
    formData.append('orderId', order.id!);
    fetcherDisplayRequisition.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [fetcherDisplayRequisition, location.pathname, order.id]);

  // Handler for UPDATE FILES action
  const handleUpdateFiles = useCallback(() => {
    setUploadFilesDialogOpen(true);
  }, [setUploadFilesDialogOpen]);

  // Handler for EDIT ORDER action
  const handleEditOrder = useCallback(() => {
    navigate(`/${organization}/orders/${order.id}/edit`, {
      state: {
        background: {
          pathname: location.pathname,
          search: location.search,
        },
      },
    });
  }, [navigate, organization, order.id, location.pathname, location.search]);

  const handleCancel = useCallback(() => {
    if (
      isCSOrSalesUser &&
      [
        WorkflowType.CLINIC,
        WorkflowType.ANCILLARY_LAB,
        WorkflowType.DIRECT_UPLOAD_LAB,
      ].includes(workflowType)
    ) {
      setCancelUploadDialogOpen(true);
    } else {
      setCancelDialogOpen(true);
    }
  }, [isCSOrSalesUser, workflowType]);

  // Handler for confirming cancel order (after confirmation dialog or after uploading cancel docs)
  const handleConfirmCancel = useCallback(() => {
    setCancelDialogOpen(false);
    setCancelUploadDialogOpen(false);
    const formData = new FormData();
    formData.append('action', OrderAction.CANCEL);
    formData.append('orderData', JSON.stringify(order));
    fetcherCancel.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [fetcherCancel, location.pathname, order]);

  // Handler for closing cancel order confirmation dialog
  const handleCloseCancelDialog = useCallback(() => {
    setCancelDialogOpen(false);
  }, []);

  // Handler for closing cancel order upload dialog
  const handleCloseCancelUploadDialog = useCallback(() => {
    setCancelUploadDialogOpen(false);
  }, []);

  // Handler for Pathology Fax Failed retry - opens confirmation dialog
  const handleOpenRefaxPathologyFilesDialog = useCallback(() => {
    setPathologyFaxDialogMode(PathologyFaxDialogMode.Refax);
  }, []);

  // Handler for REVERT TO QC PENDING action - opens confirmation dialog
  const handleRevertToQcPending = useCallback(() => {
    setRevertToQcPendingDialogOpen(true);
  }, []);

  // Handler for IMAGE QC APPROVE action - opens confirmation dialog
  const handleImageQcApprove = useCallback(() => {
    setImageQcApproveDialogOpen(true);
  }, []);

  const handleImageQcReject = useCallback(() => {
    setImageQcRejectDialogOpen(true);
  }, []);

  // Handler for REJECT DRAFT REPORT action - opens reject report form
  const handleRejectDraftReport = useCallback(() => {
    setRejectReportDialogOpen(true);
  }, []);

  // Handler for closing reject report dialog (cancelingAction equivalent)
  const handleCloseRejectReportDialog = useCallback(() => {
    setRejectReportDialogOpen(false);
  }, []);

  // Handler for confirming reject report - submit via fetcher so route action runs and revalidates
  const handleConfirmRejectReport = useCallback(
    (data: RejectReportFormData) => {
      setRejectReportDialogOpen(false);
      const formData = new FormData();
      formData.append('action', OrderAction.REJECT_DRAFT_REPORT);
      formData.append('orderId', order.id!);
      formData.append('reason', data.reason);
      if (data.revertStatus) {
        formData.append('newStatus', data.revertStatus);
      }
      fetcherRejectDraftReport.submit(formData, {
        method: 'post',
        action: location.pathname,
      });
    },
    [fetcherRejectDraftReport, location.pathname, order.id],
  );

  // Handler for DISPLAY PATIENT REPORT action
  const handleDisplayPatientReport = useCallback(() => {
    const formData = new FormData();
    formData.append('action', OrderAction.DISPLAY_PATIENT_REPORT);
    formData.append('orderId', order.id!);
    fetcherDisplayPatientReport.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [fetcherDisplayPatientReport, location.pathname, order.id]);

  // Handler for DISPLAY DE-IDENTIFIED REPORT action
  const handleDisplayDeIdentifiedReport = useCallback(() => {
    const formData = new FormData();
    formData.append('action', OrderAction.DISPLAY_DE_IDENTIFIED_REPORT);
    formData.append('orderId', order.id!);
    fetcherDisplayDeIdentifiedReport.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [fetcherDisplayDeIdentifiedReport, location.pathname, order.id]);

  // Handler for SHARE TEST REPORT action - opens dialog
  const handleShareTestReport = useCallback(() => {
    setShareTestReportDialogOpen(true);
  }, []);

  // Handler for confirming share test report
  const handleConfirmShareTestReport = useCallback(
    (data: ShareTestReportFormData) => {
      setShareTestReportDialogOpen(false);
      const formData = new FormData();
      formData.append('action', OrderAction.SHARE_TEST_REPORT);
      formData.append('orderId', order.id!);
      formData.append('recipients', JSON.stringify(data.recipients));
      fetcherShareTestReport.submit(formData, {
        method: 'post',
        action: location.pathname,
      });
    },
    [fetcherShareTestReport, location.pathname, order.id],
  );

  // Handler for closing share test report dialog
  const handleCloseShareTestReportDialog = useCallback(() => {
    setShareTestReportDialogOpen(false);
  }, []);

  // Handler for REVISE REPORT action - navigate to revise report page
  const handleReviseReport = useCallback(() => {
    navigate(`/${organization}/orders/${order.id}/revise_report`);
  }, [navigate, organization, order.id]);

  // Handler for FAX TEST REPORT TO PATHOLOGY LAB action - opens confirmation dialog
  const handleFaxTestReportToPathologyLab = useCallback(() => {
    setFaxTestReportDialogOpen(true);
  }, []);

  // Handler for REFAX REPORT TO ORDERING PROVIDER action - opens confirmation dialog
  const handleRefaxReportToOrderingProvider = useCallback(() => {
    setRefaxReportDialogOpen(true);
  }, []);

  // Handler for confirming refax report to ordering provider
  const handleConfirmRefaxReportToOrderingProvider = useCallback(() => {
    setRefaxReportDialogOpen(false);
    const formData = new FormData();
    formData.append('action', OrderAction.REFAX_REPORT_TO_ORDERING_PROVIDER);
    formData.append('orderId', order.id!);
    fetcherRefaxReport.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [fetcherRefaxReport, location.pathname, order.id]);

  // Handler for closing refax report dialog
  const handleCloseRefaxReportDialog = useCallback(() => {
    setRefaxReportDialogOpen(false);
  }, []);

  // Handler for confirming fax test report to pathology lab
  const handleConfirmFaxTestReportToPathologyLab = useCallback(() => {
    setFaxTestReportDialogOpen(false);
    const formData = new FormData();
    formData.append('action', OrderAction.FAX_TEST_REPORT_TO_PATHOLOGY_LAB);
    formData.append('orderId', order.id!);
    fetcherFaxTestReport.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [fetcherFaxTestReport, location.pathname, order.id]);

  // Handler for closing fax test report dialog
  const handleCloseFaxTestReportDialog = useCallback(() => {
    setFaxTestReportDialogOpen(false);
  }, []);

  // Handler for confirming revert to QC pending
  const handleConfirmRevertToQcPending = useCallback(() => {
    setRevertToQcPendingDialogOpen(false);
    const formData = new FormData();
    formData.append('action', OrderAction.REVERT_TO_QC_PENDING);
    formData.append('orderId', order.id!);
    fetcherRevertToQcPending.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [fetcherRevertToQcPending, location.pathname, order.id]);

  // Handler for closing revert to QC pending dialog
  const handleCloseRevertToQcPendingDialog = useCallback(() => {
    setRevertToQcPendingDialogOpen(false);
  }, []);

  // Handler for confirming Image QC Approve
  const handleConfirmImageQcApprove = useCallback(() => {
    setImageQcApproveDialogOpen(false);
    const formData = new FormData();
    formData.append('action', OrderAction.IMAGE_QC_APPROVE);
    formData.append('orderId', order.id!);
    fetcherImageQcApprove.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [fetcherImageQcApprove, location.pathname, order.id]);

  // Handler for closing Image QC Approve dialog
  const handleCloseImageQcApproveDialog = useCallback(() => {
    setImageQcApproveDialogOpen(false);
  }, []);

  // Handler for confirming Image QC Reject
  const handleConfirmImageQcReject = useCallback(() => {
    setImageQcRejectDialogOpen(false);
    const formData = new FormData();
    formData.append('action', OrderAction.IMAGE_QC_REJECT);
    formData.append('orderId', order.id!);
    formData.append('reason', IMAGE_QC_REJECT_REASON_INCORRECT_PATIENT);
    fetcherImageQcReject.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  }, [fetcherImageQcReject, location.pathname, order.id]);

  // Handler for closing Image QC Reject dialog
  const handleCloseImageQcRejectDialog = useCallback(() => {
    setImageQcRejectDialogOpen(false);
  }, []);

  const submitQcApprove = useCallback(
    (payload?: PathologyFaxConfirmPayload) => {
      const formData = new FormData();
      formData.append('action', OrderAction.UPDATE_ORDER_STATUS_QC_APPROVED);
      formData.append('orderId', order.id!);
      if (payload?.pathologyFaxAction) {
        formData.append('pathologyFaxAction', payload.pathologyFaxAction);
      }
      if (payload?.note) {
        formData.append('note', payload.note);
      }
      if (payload?.uploadedFileIds?.length) {
        formData.append(
          'uploadedFileIds',
          JSON.stringify(payload.uploadedFileIds),
        );
      }
      fetcherApproveQC.submit(formData, {
        method: 'post',
        action: location.pathname,
      });
    },
    [fetcherApproveQC, location.pathname, order.id],
  );

  // Handler for APPROVE QC action
  const handleApproveQC = useCallback(() => {
    if (shouldSkipPathologyFaxConfirmationDialog(workflowType)) {
      submitQcApprove();
      return;
    }

    setPathologyFaxDialogMode(PathologyFaxDialogMode.QcApprove);
  }, [submitQcApprove, workflowType]);

  // Handler for confirming QC approval / pathology fax retry from shared dialog
  const handleConfirmPathologyFax = useCallback(
    (payload: PathologyFaxConfirmPayload) => {
      const mode = pathologyFaxDialogMode;
      setPathologyFaxDialogMode(null);

      if (mode === PathologyFaxDialogMode.Refax) {
        const formData = new FormData();
        formData.append('action', OrderAction.REFAX_PATHOLOGY_FILES);
        formData.append('orderId', order.id!);
        formData.append('pathologyFaxAction', payload.pathologyFaxAction);
        if (payload.note) {
          formData.append('note', payload.note);
        }
        if (payload.uploadedFileIds.length > 0) {
          formData.append(
            'uploadedFileIds',
            JSON.stringify(payload.uploadedFileIds),
          );
        }
        fetcherPathologyFax.submit(formData, {
          method: 'post',
          action: location.pathname,
        });
        return;
      }

      submitQcApprove(payload);
    },
    [
      fetcherPathologyFax,
      location.pathname,
      order.id,
      pathologyFaxDialogMode,
      submitQcApprove,
    ],
  );

  // Handler for closing pathology fax confirmation dialog
  const handleClosePathologyFaxDialog = useCallback(() => {
    setPathologyFaxDialogMode(null);
  }, []);

  const processFetcherResult = useCallback(
    (
      data: unknown,
      ref: RefObject<unknown>,
      onProcess: (result: OrderActionResult) => void,
    ) => {
      if (!data || ref.current === data) return;
      ref.current = data;
      onProcess(data as OrderActionResult);
    },
    [],
  );

  // Handle result for each action's fetcher
  useEffect(() => {
    if (fetcherApproveQC.data && fetcherApproveQC.state === 'idle') {
      processFetcherResult(
        fetcherApproveQC.data,
        lastProcessedApproveQCRef,
        (result) => {
          if (result.success) {
            notifySuccess('Order QC approved successfully.');
          } else {
            notifyError('Failed to approve order for QC processing.');
          }
        },
      );
    }
  }, [fetcherApproveQC.data, fetcherApproveQC.state, processFetcherResult]);

  useEffect(() => {
    if (fetcherPathologyFax.data && fetcherPathologyFax.state === 'idle') {
      processFetcherResult(
        fetcherPathologyFax.data,
        lastProcessedPathologyFaxRef,
        (result) => {
          if (result.success) {
            notifySuccess('Pathology fax request submitted successfully.');
          } else {
            notifyError(
              result.error || 'Failed to submit pathology fax request.',
            );
          }
        },
      );
    }
  }, [
    fetcherPathologyFax.data,
    fetcherPathologyFax.state,
    processFetcherResult,
  ]);

  useEffect(() => {
    if (
      fetcherRevertToQcPending.data &&
      fetcherRevertToQcPending.state === 'idle'
    ) {
      processFetcherResult(
        fetcherRevertToQcPending.data,
        lastProcessedRevertToQcPendingRef,
        (result) => {
          if (result.success) {
            notifySuccess('Order reverted to QC Pending successfully.');
          } else {
            notifyError('Failed to revert order to QC Pending.');
          }
        },
      );
    }
  }, [
    fetcherRevertToQcPending.data,
    fetcherRevertToQcPending.state,
    processFetcherResult,
  ]);

  useEffect(() => {
    if (fetcherImageQcApprove.data && fetcherImageQcApprove.state === 'idle') {
      processFetcherResult(
        fetcherImageQcApprove.data,
        lastProcessedImageQcApproveRef,
        (result) => {
          if (result.success) {
            notifySuccess('Slide image approved successfully.');
          } else {
            notifyError(result.error || 'Failed to approve the slide image.');
          }
        },
      );
    }
  }, [
    fetcherImageQcApprove.data,
    fetcherImageQcApprove.state,
    processFetcherResult,
  ]);

  useEffect(() => {
    if (fetcherImageQcReject.data && fetcherImageQcReject.state === 'idle') {
      processFetcherResult(
        fetcherImageQcReject.data,
        lastProcessedImageQcRejectRef,
        (result) => {
          if (result.success) {
            notifySuccess('Slide image rejected for QC successfully.');
          } else {
            notifyError(result.error || 'Failed to reject slide image for QC.');
          }
        },
      );
    }
  }, [
    fetcherImageQcReject.data,
    fetcherImageQcReject.state,
    processFetcherResult,
  ]);

  useEffect(() => {
    if (fetcherCancel.data && fetcherCancel.state === 'idle') {
      processFetcherResult(
        fetcherCancel.data,
        lastProcessedCancelRef,
        (result) => {
          if (result.success) {
            notifySuccess('Order canceled successfully.');
          } else {
            notifyError(
              result.error ||
                'Sorry, this order could not be canceled at this time.',
            );
          }
        },
      );
    }
  }, [fetcherCancel.data, fetcherCancel.state, processFetcherResult]);

  useEffect(() => {
    if (
      fetcherDisplayRequisition.data &&
      fetcherDisplayRequisition.state === 'idle'
    ) {
      processFetcherResult(
        fetcherDisplayRequisition.data,
        lastProcessedDisplayRequisitionRef,
        (result) => {
          if (!result.success) {
            notifyError(
              result.error ||
                'Failed to display requisition. Please try again.',
            );
          }
        },
      );
    }
  }, [
    fetcherDisplayRequisition.data,
    fetcherDisplayRequisition.state,
    processFetcherResult,
  ]);

  useEffect(() => {
    if (
      fetcherDisplayPatientReport.data &&
      fetcherDisplayPatientReport.state === 'idle'
    ) {
      processFetcherResult(
        fetcherDisplayPatientReport.data,
        lastProcessedDisplayPatientReportRef,
        (result) => {
          if (!result.success) {
            notifyError(
              result.error ||
                'Failed to display patient report. Please try again.',
            );
          }
        },
      );
    }
  }, [
    fetcherDisplayPatientReport.data,
    fetcherDisplayPatientReport.state,
    processFetcherResult,
  ]);

  useEffect(() => {
    if (
      fetcherDisplayDeIdentifiedReport.data &&
      fetcherDisplayDeIdentifiedReport.state === 'idle'
    ) {
      processFetcherResult(
        fetcherDisplayDeIdentifiedReport.data,
        lastProcessedDisplayDeIdentifiedReportRef,
        (result) => {
          if (!result.success) {
            notifyError(
              result.error ||
                'Failed to display de-identified report. Please try again.',
            );
          }
        },
      );
    }
  }, [
    fetcherDisplayDeIdentifiedReport.data,
    fetcherDisplayDeIdentifiedReport.state,
    processFetcherResult,
  ]);

  useEffect(() => {
    if (
      fetcherShareTestReport.data &&
      fetcherShareTestReport.state === 'idle'
    ) {
      processFetcherResult(
        fetcherShareTestReport.data,
        lastProcessedShareTestReportRef,
        (result) => {
          if (result.success) {
            notifySuccess('Test report shared successfully.');
          } else {
            notifyError('Failed to share test report. Please try again.');
          }
        },
      );
    }
  }, [
    fetcherShareTestReport.data,
    fetcherShareTestReport.state,
    processFetcherResult,
  ]);

  useEffect(() => {
    if (
      fetcherRejectDraftReport.data &&
      fetcherRejectDraftReport.state === 'idle'
    ) {
      processFetcherResult(
        fetcherRejectDraftReport.data,
        lastProcessedRejectDraftReportRef,
        (result) => {
          if (result.success) {
            notifySuccess('The report has been rejected successfully.');
          } else {
            notifyError(
              result.error || 'Failed to reject report. Please try again.',
            );
          }
        },
      );
    }
  }, [
    fetcherRejectDraftReport.data,
    fetcherRejectDraftReport.state,
    processFetcherResult,
  ]);

  useEffect(() => {
    if (fetcherRefaxReport.data && fetcherRefaxReport.state === 'idle') {
      processFetcherResult(
        fetcherRefaxReport.data,
        lastProcessedRefaxReportRef,
        (result) => {
          if (result.success) {
            notifySuccess(
              'Test report has been successfully submitted to the fax service.',
            );
          } else {
            notifyError(
              'Failed to refax test report to ordering provider. Please try again.',
            );
          }
        },
      );
    }
  }, [fetcherRefaxReport.data, fetcherRefaxReport.state, processFetcherResult]);

  useEffect(() => {
    if (fetcherFaxTestReport.data && fetcherFaxTestReport.state === 'idle') {
      processFetcherResult(
        fetcherFaxTestReport.data,
        lastProcessedFaxTestReportRef,
        (result) => {
          if (result.success) {
            notifySuccess(
              'Test report has been successfully submitted to the fax service.',
            );
          } else {
            notifyError(
              'Failed to fax test report to pathology lab. Please try again.',
            );
          }
        },
      );
    }
  }, [
    fetcherFaxTestReport.data,
    fetcherFaxTestReport.state,
    processFetcherResult,
  ]);

  if (availableActions.length === 0) {
    return (
      <Box sx={{ mt: 1 }}>
        <Box>There are no actions available for this order at this time.</Box>
        {workflowType === WorkflowType.DIRECT_UPLOAD_LAB && (
          <ActionButton
            size='large'
            sx={{ mt: 1 }}
            onClick={() => navigate(`/${organization}/orders`)}
          >
            BACK TO ORDERS
          </ActionButton>
        )}
      </Box>
    );
  }

  return (
    <ActionsContainer>
      <Stack spacing={0.5}>
        {availableActions.map((action, index) => {
          let button: React.ReactElement | null = null;

          switch (action) {
            case LdtOrderAction.SlideImageSection:
              button = <LdtSlideImageSection key={action} order={order} />;
              break;
            case LdtOrderAction.DisplayRequisition:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleDisplayRequisition}
                  disabled={fetcherDisplayRequisition.state !== 'idle'}
                >
                  DISPLAY REQUISITION
                </ActionButton>
              );
              break;
            case LdtOrderAction.UpdateFiles:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleUpdateFiles}
                >
                  UPDATE FILES
                </ActionButton>
              );
              break;
            case LdtOrderAction.EditOrder:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleEditOrder}
                >
                  EDIT ORDER
                </ActionButton>
              );
              break;
            case LdtOrderAction.Cancel:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleCancel}
                  disabled={fetcherCancel.state !== 'idle'}
                >
                  CANCEL
                </ActionButton>
              );
              break;
            case LdtOrderAction.RefaxPathologyFiles:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleOpenRefaxPathologyFilesDialog}
                  disabled={fetcherPathologyFax.state !== 'idle'}
                >
                  REFAX PATHOLOGY FILES
                </ActionButton>
              );
              break;
            case LdtOrderAction.RevertToQcPending:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleRevertToQcPending}
                  disabled={fetcherRevertToQcPending.state !== 'idle'}
                >
                  REVERT TO QC PENDING
                </ActionButton>
              );
              break;
            case LdtOrderAction.ApproveQC:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleApproveQC}
                  disabled={fetcherApproveQC.state !== 'idle'}
                >
                  QC APPROVE
                </ActionButton>
              );
              break;
            case LdtOrderAction.RejectDraftReport:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleRejectDraftReport}
                  disabled={fetcherRejectDraftReport.state !== 'idle'}
                >
                  REJECT DRAFT REPORT
                </ActionButton>
              );
              break;
            case LdtOrderAction.DisplayPatientReport:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleDisplayPatientReport}
                  disabled={fetcherDisplayPatientReport.state !== 'idle'}
                >
                  DISPLAY PATIENT REPORT
                </ActionButton>
              );
              break;
            case LdtOrderAction.DisplayDeIdentifiedReport:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleDisplayDeIdentifiedReport}
                  disabled={fetcherDisplayDeIdentifiedReport.state !== 'idle'}
                >
                  DISPLAY DE-IDENTIFIED REPORT
                </ActionButton>
              );
              break;
            case LdtOrderAction.ShareTestReport:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleShareTestReport}
                  disabled={fetcherShareTestReport.state !== 'idle'}
                >
                  SHARE TEST REPORT
                </ActionButton>
              );
              break;
            case LdtOrderAction.ReviseReportMutation:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleReviseReport}
                >
                  REVISE REPORT
                </ActionButton>
              );
              break;
            case LdtOrderAction.FaxTestReportToPathologyLabMutation:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleFaxTestReportToPathologyLab}
                  disabled={fetcherFaxTestReport.state !== 'idle'}
                >
                  FAX TEST REPORT TO PATHOLOGY LAB
                </ActionButton>
              );
              break;
            case LdtOrderAction.RefaxReportToOrderingProvider:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleRefaxReportToOrderingProvider}
                  disabled={fetcherRefaxReport.state !== 'idle'}
                >
                  REFAX REPORT TO ORDERING PROVIDER
                </ActionButton>
              );
              break;
            case LdtOrderAction.ImageQcApprove:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleImageQcApprove}
                  disabled={fetcherImageQcApprove.state !== 'idle'}
                >
                  IMAGE QC APPROVE
                </ActionButton>
              );
              break;
            case LdtOrderAction.ImageQcReject:
              button = (
                <ActionButton
                  key={action}
                  size='large'
                  onClick={handleImageQcReject}
                  disabled={fetcherImageQcReject.state !== 'idle'}
                >
                  IMAGE QC REJECT
                </ActionButton>
              );
              break;
            case LdtOrderAction.DownloadSlideImageQuery: {
              const slideImage = findMostRecentSlideImage(order)!;
              button = (
                <SlideImageDownloadRow
                  key={action}
                  fileId={slideImage.id}
                  filename={slideImage.filename}
                  status={slideImage.status}
                />
              );
              break;
            }
          }

          return (
            <ActionItemContainer key={action}>
              {button}
              {index < availableActions.length - 1 && <ActionDivider />}
            </ActionItemContainer>
          );
        })}
      </Stack>

      {/* Upload Files Dialog */}
      <UploadFilesDialog
        open={uploadFilesDialogOpen}
        onClose={() => setUploadFilesDialogOpen(false)}
        order={order}
        orderRepository={orderRepository}
        workflowType={workflowType}
      />

      {/* Cancel Order Upload Dialog - for Artera (CS/Sales) users: upload cancel docs then cancel */}
      <CancelOrderUploadDialog
        open={cancelUploadDialogOpen}
        onClose={handleCloseCancelUploadDialog}
        onConfirmCancel={handleConfirmCancel}
        order={order}
        orderRepository={orderRepository}
      />

      {/* Pathology fax confirmation (QC Approve + Pathology Fax Failed retry) */}
      <ConfirmPathologyFaxDialog
        open={pathologyFaxDialogMode != null}
        onClose={handleClosePathologyFaxDialog}
        onConfirm={handleConfirmPathologyFax}
        order={order}
        orderRepository={orderRepository}
        title={
          pathologyFaxDialogMode === PathologyFaxDialogMode.Refax
            ? 'Confirm Pathology Fax Retry'
            : 'Confirm QC Approval'
        }
        isSubmitting={
          pathologyFaxDialogMode === PathologyFaxDialogMode.Refax
            ? fetcherPathologyFax.state !== 'idle'
            : fetcherApproveQC.state !== 'idle'
        }
      />

      {/* Revert to QC Pending Confirmation Dialog */}
      <FeedbackPopup
        data={{
          primaryButtonText: 'CONTINUE',
          secondaryButtonText: 'CANCEL',
          title: 'Confirm Action',
          subtitle:
            'Are you sure you want to revert this order to Quality Control Pending?',
          icon: Warning,
          type: FeedbackType.Primary,
        }}
        isDialogOpen={revertToQcPendingDialogOpen}
        onPrimaryButtonClick={handleConfirmRevertToQcPending}
        onSecondaryButtonClick={handleCloseRevertToQcPendingDialog}
        handleDialogClose={handleCloseRevertToQcPendingDialog}
      />

      {/* Image QC Approve Confirmation Dialog */}
      <FeedbackPopup
        data={{
          primaryButtonText: 'CONTINUE',
          secondaryButtonText: 'CANCEL',
          title: 'Confirm Image QC Approve',
          subtitle:
            'Are you sure you want to approve the slide image for QC? This will start AI processing.',
          icon: Warning,
          type: FeedbackType.Primary,
        }}
        isDialogOpen={imageQcApproveDialogOpen}
        onPrimaryButtonClick={handleConfirmImageQcApprove}
        onSecondaryButtonClick={handleCloseImageQcApproveDialog}
        handleDialogClose={handleCloseImageQcApproveDialog}
      />

      {/* Image QC Reject Confirmation Dialog */}
      <FeedbackPopup
        data={{
          primaryButtonText: 'CONTINUE',
          secondaryButtonText: 'CANCEL',
          title: IMAGE_QC_REJECT_DIALOG_TITLE,
          subtitle: 'Are you sure you would like to reject this slide image?',
          icon: Warning,
          type: FeedbackType.Primary,
        }}
        isDialogOpen={imageQcRejectDialogOpen}
        onPrimaryButtonClick={handleConfirmImageQcReject}
        onSecondaryButtonClick={handleCloseImageQcRejectDialog}
        handleDialogClose={handleCloseImageQcRejectDialog}
      />

      {/* Fax Test Report to Pathology Lab Confirmation Dialog */}
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          title: 'Fax Test Report to Pathology Lab',
          subtitle:
            'Are you sure you want to fax the test report to the pathology lab?',
          icon: Print,
          type: FeedbackType.Primary,
        }}
        isDialogOpen={faxTestReportDialogOpen}
        onPrimaryButtonClick={handleConfirmFaxTestReportToPathologyLab}
        onSecondaryButtonClick={handleCloseFaxTestReportDialog}
        handleDialogClose={handleCloseFaxTestReportDialog}
      />

      {/* Refax Report to Ordering Provider Confirmation Dialog */}
      <FeedbackPopup
        data={{
          primaryButtonText: 'CONTINUE',
          secondaryButtonText: 'CANCEL',
          title: 'Confirm Action',
          subtitle: 'Confirm refax TRR?',
          icon: Caution,
          type: FeedbackType.Primary,
        }}
        isDialogOpen={refaxReportDialogOpen}
        onPrimaryButtonClick={handleConfirmRefaxReportToOrderingProvider}
        onSecondaryButtonClick={handleCloseRefaxReportDialog}
        handleDialogClose={handleCloseRefaxReportDialog}
      />

      {/* Share Test Report Dialog */}
      <ShareTestReportDialog
        open={shareTestReportDialogOpen}
        onClose={handleCloseShareTestReportDialog}
        onShare={handleConfirmShareTestReport}
      />

      {/* Reject Report Dialog */}
      <RejectReportDialog
        open={rejectReportDialogOpen}
        onClose={handleCloseRejectReportDialog}
        onSubmit={handleConfirmRejectReport}
        order={order}
      />

      {/* Cancel Order Confirmation Dialog */}
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, I CONFIRM',
          secondaryButtonText: 'NO',
          title: 'Cancel Order',
          subtitle:
            'Are you sure you want to cancel this order? This action cannot be undone.',
          icon: Caution,
          type: FeedbackType.Primary,
        }}
        minHeight='240px'
        isDialogOpen={cancelDialogOpen}
        onPrimaryButtonClick={handleConfirmCancel}
        onSecondaryButtonClick={handleCloseCancelDialog}
        handleDialogClose={handleCloseCancelDialog}
      />
    </ActionsContainer>
  );
};
